/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type ApplicationInstance from "@ember/application/instance";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { ValidatorMap } from "ember-changeset/types";
import { task } from "ember-concurrency";
import type { IntlService } from "ember-intl";
import type ApplicationAdapter from "parog-web/adapters/application";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import JournalEventBaseComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import type ConsultationReportWrittenQuestion from "parog-web/models/consultation/consultation-report-written-question";
import tWithDefault from "parog-web/utils/t-with-default";

import styles from "./index.module.css";

interface ConsultationReportFormComponentArgs extends ForcBaseArgs {
  consultationType: string;
  resetDraft: boolean;
}

export default class ConsultationReportFormComponent extends JournalEventBaseComponent<ConsultationReportFormComponentArgs> {
  styles = styles;

  static fullComponentId = "consultation/report-form";
  static forcDisplayName = "Compte rendu de consultation";
  static journalEventClass = ConsultationReportWrittenJournalEvent;

  @tracked declare consultationReportIsDirty: boolean;
  @tracked currentQuestions: ConsultationReportWrittenQuestion[] = [];

  @service declare intl: IntlService;

  applicationAdapter = (getOwner(this) as ApplicationInstance).lookup(
    "adapter:application"
  ) as ApplicationAdapter;

  journalEventProperties = [
    "occurredAt",
    "consultationType",
    "actionsCarriedOut",
    "questions"
  ];

  retrieveJournalEvent = JournalEventRetrievalStrategies.sourceJournalEvent;

  autoSaveDraft = true;

  get changeset() {
    const changeset = super.changeset;
    if (this.args.resetDraft && this.draft) {
      this.draft.clear();
    }
    return changeset;
  }

  get consultationLabel(): string {
    const consultationType = this.changeset.get("consultationType");
    return tWithDefault(
      this.intl,
      `consultation-history.${consultationType}`,
      consultationType
    );
  }

  get comesFromDashboard(): boolean {
    const consultationType = this.changeset.get("consultationType");
    return !!this.intl.lookup(
      `consultation-history.${consultationType}`,
      "fr-fr",
      { resilient: true }
    );
  }

  get validations(): ValidatorMap {
    return this.journalEventClass.validations(this.intl);
  }

  retrieveModel(): Record<string, unknown> {
    const model = super.retrieveModel();
    if (this.isNewMode) {
      model.questions = ConsultationReportWrittenJournalEvent.getQuestions(
        this.args.consultationType
      );
      model.occurredAt = new Date();
      model.consultationType = this.args.consultationType;
    }

    return model;
  }

  @action
  setCurrentQuestions(questions: ConsultationReportWrittenQuestion[]): void {
    this.currentQuestions = questions;
  }

  get questions(): ConsultationReportWrittenQuestion[] {
    // We only provide the questions that are in relation with
    // the current consultationType
    const currentQuestionsInChangeset = this.changeset.get("questions");

    return this.currentQuestions?.map(currentQuestion => {
      // If the question exists in the changeset (i.e. in the draft)
      // We provide it
      const alreadyExistingQuestionInChangeset =
        currentQuestionsInChangeset.find(q => q.key == currentQuestion.key);
      if (alreadyExistingQuestionInChangeset) {
        return alreadyExistingQuestionInChangeset;
      }
      // Otherwise, we provide the default question
      return currentQuestion;
    });
  }

  // As we store every questions, even when changing consultation type
  // We just want the ones we are currently editing
  async save(): Promise<ConsultationReportWrittenJournalEvent> {
    const questionsChangeset = this.changeset.get("questions");
    const curatedQuestionsChangeset = questionsChangeset.filter(question =>
      this.currentQuestions.map(q => q.key).includes(question.key)
    );
    this.changeset.set("questions", curatedQuestionsChangeset);

    return await super.save();
  }

  @action
  updateQuestion(): void {
    this.consultationReportIsDirty = true;
  }

  get isDirty(): boolean {
    return this.consultationReportIsDirty ? true : super.isDirty;
  }

  generatePdf = task(async (_e: Event) => {
    fetch(
      `/api/v1/consultation-report-written-journal-events/${this.sourceJeId}.pdf`,
      {
        method: "GET",
        headers: Object.assign(
          {
            "Content-Type": "application/json"
          },
          this.applicationAdapter.headers
        )
      }
    )
      .then(response => {
        if (response.ok) {
          return response.blob();
        }
        throw new Error("Téléchargement échoué");
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Compte rendu ${this.changeset.consultationType} ${this.patient.fullName}`;
        a.click();
        window.URL.revokeObjectURL(url);

        this.flashMessages.success("Le PDF a été téléchargé avec succès.", {
          title: "Téléchargement terminé"
        });
      })
      .catch(() => {
        this.flashMessages.warning(
          "Une erreur est survenue lors du téléchargement.",
          {
            title: "Erreur"
          }
        );
      });
  });
}
